import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const prev = useCallback(() => onNavigate((index - 1 + images.length) % images.length), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % images.length), [index, images.length, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  return (
    <AnimatePresence>
      {index !== null && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[95] bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center justify-center"
          onClick={onClose}
        >
          <motion.img
            key={index}
            src={images[index]}
            alt="PT Exports leather jacket — full view"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.25, 1, 0.5, 1] }}
            className="max-h-[84vh] max-w-[88vw] object-contain shadow-[0_40px_120px_rgba(0,0,0,0.9)]"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 rounded-full border border-[#F5F5F0]/20 bg-[#0A0A0A]/50 flex items-center justify-center text-[#F5F5F0] hover:border-[#B89B72] hover:text-[#B89B72] transition-colors duration-300"
          >
            <X className="w-5 h-5" strokeWidth={1.2} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[#F5F5F0]/20 bg-[#0A0A0A]/50 flex items-center justify-center text-[#F5F5F0] hover:border-[#B89B72] hover:text-[#B89B72] transition-colors duration-300"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={1.2} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[#F5F5F0]/20 bg-[#0A0A0A]/50 flex items-center justify-center text-[#F5F5F0] hover:border-[#B89B72] hover:text-[#B89B72] transition-colors duration-300"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={1.2} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-accent text-[10px] text-[#A3A3A3]" data-testid="lightbox-counter">
            {String(index + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')} — PT EXPORTS ARCHIVE
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
